import * as THREE from 'three';
import { CONFIG } from '../../config';
import type { GameState } from '../../state/game-state';

type WeaponVisual = {
  mesh: THREE.Mesh;
  baseAngle: number;
  color: number;
  type: 'sword' | 'axe' | 'crystal';
};

function makeSword(color: number): THREE.Mesh {
  const group = new THREE.Group();
  const bladeGeo = new THREE.ConeGeometry(0.05, 0.6, 6);
  const blade = new THREE.Mesh(
    bladeGeo,
    new THREE.MeshBasicMaterial({ color })
  );
  blade.position.y = 0.3;
  group.add(blade);
  const guardGeo = new THREE.BoxGeometry(0.2, 0.04, 0.04);
  const guard = new THREE.Mesh(guardGeo, new THREE.MeshBasicMaterial({ color: 0x2a1810 }));
  guard.position.y = 0;
  group.add(guard);
  const handleGeo = new THREE.CylinderGeometry(0.025, 0.025, 0.18, 6);
  const handle = new THREE.Mesh(
    handleGeo,
    new THREE.MeshBasicMaterial({ color: 0x2a1810 })
  );
  handle.position.y = -0.09;
  group.add(handle);

  const wrapper = new THREE.Group();
  wrapper.add(group);
  return wrapper as unknown as THREE.Mesh;
}

function makeAxe(color: number): THREE.Mesh {
  const group = new THREE.Group();
  const handleGeo = new THREE.CylinderGeometry(0.03, 0.03, 0.55, 6);
  const handle = new THREE.Mesh(
    handleGeo,
    new THREE.MeshBasicMaterial({ color: 0x2a1810 })
  );
  group.add(handle);
  const headShape = new THREE.Shape();
  headShape.moveTo(0, 0);
  headShape.bezierCurveTo(0.15, 0.05, 0.2, 0.2, 0.05, 0.3);
  headShape.bezierCurveTo(-0.05, 0.2, -0.1, 0.05, 0, 0);
  const headGeo = new THREE.ShapeGeometry(headShape);
  const head = new THREE.Mesh(
    headGeo,
    new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide })
  );
  head.position.y = 0.25;
  group.add(head);

  const wrapper = new THREE.Group();
  wrapper.add(group);
  return wrapper as unknown as THREE.Mesh;
}

function makeCrystal(color: number): THREE.Mesh {
  const geo = new THREE.OctahedronGeometry(0.12, 0);
  const mesh = new THREE.Mesh(
    geo,
    new THREE.MeshBasicMaterial({ color })
  );
  return mesh;
}

export class Weapons {
  public root: THREE.Group;
  private weapons: WeaponVisual[] = [];
  private autoMeleeLastAt = 0;
  private phase = 0;
  private maxWeapons = 8;

  constructor() {
    this.root = new THREE.Group();
    for (let i = 0; i < this.maxWeapons; i++) {
      const typeRoll = i % 3;
      let mesh: THREE.Mesh;
      let color: number;
      let wtype: WeaponVisual['type'];
      if (typeRoll === 0) {
        color = 0xfca;
        mesh = makeSword(color);
        wtype = 'sword';
      } else if (typeRoll === 1) {
        color = 0xfb6;
        mesh = makeAxe(color);
        wtype = 'axe';
      } else {
        color = 0xff8;
        mesh = makeCrystal(color);
        wtype = 'crystal';
      }
      const baseAngle = (i / this.maxWeapons) * Math.PI * 2;
      this.weapons.push({ mesh, baseAngle, color, type: wtype });
      this.root.add(mesh);
    }
    this.setVisibleCount(1);
  }

  private setVisibleCount(n: number): void {
    for (let i = 0; i < this.weapons.length; i++) {
      this.weapons[i].mesh.visible = i < n;
    }
  }

  setCenter(pos: THREE.Vector3): void {
    this.root.position.copy(pos);
  }

  update(now: number, state: GameState, deltaMs: number): void {
    this.setVisibleCount(state.weaponCount);
    this.phase += deltaMs * 0.001;
    const baseRadius = 1.4;
    const speed = 0.5 + state.corruption / CONFIG.corruption.max * 1.5;

    for (let i = 0; i < this.weapons.length; i++) {
      const w = this.weapons[i];
      if (!w.mesh.visible) continue;
      const angle = w.baseAngle + this.phase * speed;
      const r = baseRadius + 0.1 * Math.sin(this.phase * 2 + i);
      w.mesh.position.set(Math.cos(angle) * r, Math.sin(this.phase * 1.5 + i) * 0.15, Math.sin(angle) * r);
      w.mesh.rotation.y = angle + Math.PI / 2;
      w.mesh.rotation.x = Math.sin(this.phase * 2 + i) * 0.4;
      w.mesh.rotation.z = Math.cos(this.phase * 2 + i) * 0.4;

      const intensity = 1 + state.corruption / CONFIG.corruption.max * 0.5;
      w.mesh.scale.setScalar(0.9 + 0.1 * Math.sin(this.phase * 4 + i) * intensity);
    }
  }

  applyAutoMelee(
    now: number,
    dinos: { position: [number, number, number] }[],
    onHit: (pos: [number, number, number]) => void
  ): void {
    if (now - this.autoMeleeLastAt < CONFIG.weapons.autoMeleeIntervalMs) return;
    const center = this.root.position;
    for (const d of dinos) {
      const dx = d.position[0] - center.x;
      const dz = d.position[2] - center.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist > CONFIG.weapons.autoMeleeRange) continue;
      onHit(d.position);
    }
    this.autoMeleeLastAt = now;
  }
}
